/**
 * SourcePaneContext — right-pane state for the two-pane chat.
 *
 * Holds the LRU-ordered stack of sources the user has clicked through citation
 * chips, plus PR4's `pendingAction` slot. Matches the project's existing
 * Context-only pattern (ThemeProvider / AuthProvider / SessionProvider /
 * ToastProvider). Intentionally NOT Zustand — keeps state surface uniform.
 *
 * State persists per-session via localStorage so reloading the page keeps
 * the user's open sources around. The pane width persists globally.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type ReactNode,
} from 'react'
import type { ActionCard, Citation } from '../types/cards'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_TABS = 10 // LRU cap; oldest tab is evicted when an 11th is opened
const WIDTH_KEY = 'trendy_source_pane_width'
const COLLAPSED_KEY = 'trendy_source_pane_collapsed'
const DEFAULT_WIDTH = 480
const MIN_WIDTH = 320
const MAX_WIDTH_RATIO = 0.7 // % of viewport — pane never wider than this

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type PaneMode = 'source' | 'action'

export interface SourcePaneState {
  openSources: Citation[]
  activeSourceId: string | null
  pendingAction: ActionCard | null
  // PR4: approval_id paired with pendingAction. Set together via
  // setPendingAction(card, approvalId). Cleared together via setPendingAction(null).
  pendingActionApprovalId: string | null
  // When the gate stops accepting this approval, as an ISO string from the
  // server. Carried in state so the collapsed rail can show the remaining time
  // — a parked approval that expires with no warning is the whole failure this
  // is meant to prevent.
  pendingActionExpiresAt: string | null
  isCollapsed: boolean
  width: number
  mode: PaneMode
}

type Action =
  | { type: 'OPEN'; citation: Citation }
  | { type: 'CLOSE'; id: string }
  | { type: 'CLOSE_ALL' }
  | { type: 'ACTIVATE'; id: string }
  | { type: 'TOGGLE_COLLAPSED' }
  | { type: 'SET_COLLAPSED'; value: boolean }
  | { type: 'SET_WIDTH'; value: number }
  | {
      type: 'SET_PENDING_ACTION'
      action: ActionCard | null
      approvalId: string | null
      expiresAt?: string | null
    }
  | { type: 'CYCLE'; direction: 1 | -1 }

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

function clampWidth(w: number): number {
  const max = typeof window !== 'undefined'
    ? window.innerWidth * MAX_WIDTH_RATIO
    : Number.POSITIVE_INFINITY
  return Math.min(max, Math.max(MIN_WIDTH, Math.round(w)))
}

function reducer(state: SourcePaneState, action: Action): SourcePaneState {
  switch (action.type) {
    case 'OPEN': {
      const { citation } = action
      // Dedup by id — if already open, just activate it (LRU bumps to front)
      const existingIndex = state.openSources.findIndex(s => s.id === citation.id)
      let next: Citation[]
      if (existingIndex >= 0) {
        next = [
          citation,
          ...state.openSources.slice(0, existingIndex),
          ...state.openSources.slice(existingIndex + 1),
        ]
      } else {
        next = [citation, ...state.openSources].slice(0, MAX_TABS)
      }
      return {
        ...state,
        openSources: next,
        activeSourceId: citation.id,
        isCollapsed: false,
        mode: state.pendingAction ? 'action' : 'source',
      }
    }
    case 'CLOSE': {
      const next = state.openSources.filter(s => s.id !== action.id)
      let activeId = state.activeSourceId
      if (activeId === action.id) {
        activeId = next.length > 0 ? next[0]!.id : null
      }
      return { ...state, openSources: next, activeSourceId: activeId }
    }
    case 'CLOSE_ALL':
      return { ...state, openSources: [], activeSourceId: null }
    case 'ACTIVATE':
      if (!state.openSources.some(s => s.id === action.id)) return state
      return { ...state, activeSourceId: action.id, mode: 'source', isCollapsed: false }
    case 'TOGGLE_COLLAPSED':
      return { ...state, isCollapsed: !state.isCollapsed }
    case 'SET_COLLAPSED':
      return { ...state, isCollapsed: action.value }
    case 'SET_WIDTH':
      return { ...state, width: clampWidth(action.value) }
    case 'SET_PENDING_ACTION':
      return {
        ...state,
        pendingAction: action.action,
        pendingActionApprovalId: action.approvalId,
        pendingActionExpiresAt: action.expiresAt ?? null,
        mode: action.action ? 'action' : (state.openSources.length > 0 ? 'source' : state.mode),
        isCollapsed: action.action ? false : state.isCollapsed,
      }
    case 'CYCLE': {
      if (state.openSources.length === 0) return state
      const i = state.openSources.findIndex(s => s.id === state.activeSourceId)
      const len = state.openSources.length
      const next = i < 0 ? 0 : (i + action.direction + len) % len
      return { ...state, activeSourceId: state.openSources[next]!.id }
    }
    default:
      return state
  }
}

// ---------------------------------------------------------------------------
// Initial state — reads localStorage for width + collapsed only.
// Tabs are intentionally NOT persisted across reloads (they're tied to a
// specific assistant message that may no longer be in scroll view).
// ---------------------------------------------------------------------------

function readInitial(): SourcePaneState {
  let width = DEFAULT_WIDTH
  let isCollapsed = true
  try {
    const w = localStorage.getItem(WIDTH_KEY)
    if (w) {
      const parsed = parseInt(w, 10)
      if (!Number.isNaN(parsed)) width = parsed
    }
    isCollapsed = localStorage.getItem(COLLAPSED_KEY) === '1'
  } catch {
    // ignore — SSR / sandboxed environments
  }
  return {
    openSources: [],
    activeSourceId: null,
    pendingAction: null,
    pendingActionApprovalId: null,
    pendingActionExpiresAt: null,
    isCollapsed,
    width: clampWidth(width),
    mode: 'source',
  }
}

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

export interface SourcePaneContextValue extends SourcePaneState {
  openSource: (citation: Citation) => void
  closeSource: (id: string) => void
  closeAll: () => void
  activateSource: (id: string) => void
  toggleCollapsed: () => void
  setCollapsed: (value: boolean) => void
  setWidth: (value: number) => void
  setPendingAction: (
    action: ActionCard | null,
    approvalId?: string | null,
    expiresAt?: string | null,
  ) => void
  cycle: (direction: 1 | -1) => void
  activeSource: Citation | null
}

const SourcePaneContext = createContext<SourcePaneContextValue | null>(null)

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function SourcePaneProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, undefined, readInitial)

  // Persist width + collapsed to localStorage
  const widthRef = useRef(state.width)
  widthRef.current = state.width
  useEffect(() => {
    try {
      localStorage.setItem(WIDTH_KEY, String(state.width))
    } catch { /* noop */ }
  }, [state.width])
  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, state.isCollapsed ? '1' : '0')
    } catch { /* noop */ }
  }, [state.isCollapsed])

  // Keyboard shortcuts — Esc collapses, [ / ] cycle tabs.
  // Ignored when focus is in an editable element (matches the a11y rule
  // listed in the plan's cross-cutting requirements).
  useEffect(() => {
    function inEditable(el: EventTarget | null): boolean {
      if (!(el instanceof HTMLElement)) return false
      const tag = el.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
      if (el.isContentEditable) return true
      return false
    }
    function onKey(e: KeyboardEvent) {
      if (inEditable(e.target)) return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if (e.key === 'Escape') {
        // Only collapse if pane is currently open
        if (!state.isCollapsed && (state.openSources.length > 0 || state.pendingAction)) {
          dispatch({ type: 'SET_COLLAPSED', value: true })
          e.preventDefault()
        }
        return
      }
      if (e.key === ']') {
        dispatch({ type: 'CYCLE', direction: 1 })
        e.preventDefault()
      } else if (e.key === '[') {
        dispatch({ type: 'CYCLE', direction: -1 })
        e.preventDefault()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [state.isCollapsed, state.openSources.length, state.pendingAction])

  const openSource = useCallback((citation: Citation) => {
    dispatch({ type: 'OPEN', citation })
  }, [])
  const closeSource = useCallback((id: string) => {
    dispatch({ type: 'CLOSE', id })
  }, [])
  const closeAll = useCallback(() => {
    dispatch({ type: 'CLOSE_ALL' })
  }, [])
  const activateSource = useCallback((id: string) => {
    dispatch({ type: 'ACTIVATE', id })
  }, [])
  const toggleCollapsed = useCallback(() => {
    dispatch({ type: 'TOGGLE_COLLAPSED' })
  }, [])
  const setCollapsed = useCallback((value: boolean) => {
    dispatch({ type: 'SET_COLLAPSED', value })
  }, [])
  const setWidth = useCallback((value: number) => {
    dispatch({ type: 'SET_WIDTH', value })
  }, [])
  const setPendingAction = useCallback((
    action: ActionCard | null,
    approvalId: string | null = null,
    expiresAt: string | null = null,
  ) => {
    dispatch({
      type: 'SET_PENDING_ACTION',
      action,
      approvalId: action ? approvalId : null,
      expiresAt: action ? expiresAt : null,
    })
  }, [])
  const cycle = useCallback((direction: 1 | -1) => {
    dispatch({ type: 'CYCLE', direction })
  }, [])

  const activeSource = useMemo<Citation | null>(() => {
    if (!state.activeSourceId) return null
    return state.openSources.find(s => s.id === state.activeSourceId) ?? null
  }, [state.activeSourceId, state.openSources])

  const value = useMemo<SourcePaneContextValue>(() => ({
    ...state,
    activeSource,
    openSource,
    closeSource,
    closeAll,
    activateSource,
    toggleCollapsed,
    setCollapsed,
    setWidth,
    setPendingAction,
    cycle,
  }), [state, activeSource, openSource, closeSource, closeAll, activateSource,
       toggleCollapsed, setCollapsed, setWidth, setPendingAction, cycle])

  return (
    <SourcePaneContext.Provider value={value}>
      {children}
    </SourcePaneContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useSourcePane(): SourcePaneContextValue {
  const ctx = useContext(SourcePaneContext)
  if (!ctx) {
    throw new Error(
      'useSourcePane() must be used inside a <SourcePaneProvider>. ' +
      'Wrap your route in src/pages/ChatApp.jsx.'
    )
  }
  return ctx
}

/** Read-only variant for components that may render outside the provider
 * (e.g. a chip in a portal or a story fixture). Returns null when no
 * provider is mounted. */
export function useSourcePaneOptional(): SourcePaneContextValue | null {
  return useContext(SourcePaneContext)
}
